/**
 * Canonical triage counts (TASK-653).
 *
 * THE primary attention number, product-wide, is the count of UNRESOLVED ALERT
 * GROUPS: active (not dismissed/resolved/expired-snoozed) alerts, collapsed by
 * their cause via groupAlerts. Header bell, Change Radar, the Alerts page, and
 * Compliance all show this same number; any other count (signals by severity,
 * affected clients, open tasks) is a labeled secondary projection of the same
 * records. Definitions live in docs/design/triage-glossary.md.
 */
import type { DashboardAlert } from '../types/dashboard';
import { groupAlerts, type AlertGroup } from './groupAlerts';

/**
 * Unresolved = still requiring attention: `active` OR `read` (acknowledged but
 * not handled), plus snoozes that have expired. Dismissed and resolved alerts
 * are out. This matches the backend count semantics (get_counts spans
 * active+read) — an acknowledged alert must NOT vanish from triage.
 */
export function isUnresolved(alert: DashboardAlert): boolean {
  if (alert.status) {
    if (alert.status === 'dismissed' || alert.status === 'resolved') return false;
    if (alert.status === 'snoozed') {
      const until = alert.snoozedUntil ? Date.parse(alert.snoozedUntil) : NaN;
      return Number.isFinite(until) ? until <= Date.now() : false;
    }
    return true; // active | read
  }
  return !alert.isDismissed;
}

/** Unresolved alerts — dismissed/resolved/still-snoozed excluded. */
export function unresolvedAlerts(alerts: DashboardAlert[]): DashboardAlert[] {
  return alerts.filter(isUnresolved);
}

/** Unresolved alerts collapsed by cause — the triage unit RMs act on. */
export function unresolvedGroups(alerts: DashboardAlert[]): AlertGroup[] {
  return groupAlerts(unresolvedAlerts(alerts));
}

/** Unresolved groups derivable from the alerts currently in the store. */
export function unresolvedGroupCount(alerts: DashboardAlert[]): number {
  return unresolvedGroups(alerts).length;
}

/**
 * The one primary count every surface shows.
 *
 * Deep review #6: this used to be `unresolvedGroupCount(alerts)` — grouped in the browser over
 * the fetched PAGE. `GET /api/alerts` caps at `limit=200`, and a real book holds more than that
 * (521 live alerts on the demo account), so whole causes fell off the end: the four surfaces
 * agreed on 11 where the truth was 12. No page size fixes that — the server counts every row
 * (`AlertService.get_counts`). The local grouping stays for RENDERING the visible groups; only
 * the headline number comes from the server, and only falls back before `/counts` answers.
 */
export function primaryTriageCount(
  alerts: DashboardAlert[],
  counts?: { unresolved_groups?: number },
): number {
  return counts?.unresolved_groups ?? unresolvedGroupCount(alerts);
}
